import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn';
import type { PositionRow } from '../../types';
import { POSITION_STATUS } from '../../constants/tradingConstants';
import PnlFlash from '../common/PnlFlash';

interface TodayPnlCardsProps {
  positions: PositionRow[];
  loading?: boolean;
}

const TodayPnlCards: React.FC<TodayPnlCardsProps> = ({ positions, loading }) => {
  const closed = positions.filter(p => p.status === POSITION_STATUS.CLOSED);
  // EXIT_PENDING positions still hold market exposure — count as unrealized.
  const open = positions.filter(
    p => p.status === POSITION_STATUS.OPEN || p.status === POSITION_STATUS.EXIT_PENDING
  );

  const realizedPnl = closed.reduce((sum, p) => sum + (p.pnl ?? 0), 0);
  const unrealizedPnl = open.reduce((sum, p) => sum + (p.pnl ?? 0), 0);
  const totalPnl = realizedPnl + unrealizedPnl;

  const profitable = closed.filter(p => p.pnl !== null && p.pnl > 0).length;
  const totalClosed = closed.length;
  const winRate = totalClosed > 0 ? Math.round((profitable / totalClosed) * 100) : 0;

  const totalPositions = positions.length;

  const placeholder = loading ? '\u2014' : undefined;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex flex-wrap">
        {/* Total P&L — prominent */}
        <div className="w-1/3 sm:w-auto sm:flex-1 p-4 border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-gray-700">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total P&L</div>
          <div className="mt-1">
            {placeholder
              ? <span className="text-2xl font-bold tabular-nums text-gray-400">{placeholder}</span>
              : <PnlFlash value={totalPnl} className="text-2xl font-bold" />}
          </div>
        </div>

        {/* Realized */}
        <div className="w-1/3 sm:w-auto sm:flex-1 p-4 border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-gray-700">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Realized</div>
          <div className="mt-1">
            {placeholder
              ? <span className="text-sm font-semibold tabular-nums text-gray-400">{placeholder}</span>
              : <PnlFlash value={realizedPnl} className="text-sm font-semibold" />}
          </div>
        </div>

        {/* Unrealized */}
        <div className="w-1/3 sm:w-auto sm:flex-1 p-4 border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-gray-700">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Unrealized</div>
          <div className="mt-1">
            {placeholder
              ? <span className="text-sm font-semibold tabular-nums text-gray-400">{placeholder}</span>
              : <PnlFlash value={unrealizedPnl} className="text-sm font-semibold" />}
          </div>
        </div>

        {/* Win Rate */}
        <div className="w-1/3 sm:w-auto sm:flex-1 p-4 border-r border-gray-200 dark:border-gray-700">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Win Rate</div>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className={cn('text-sm font-semibold tabular-nums', loading ? 'text-gray-400' : winRate >= 50 ? 'text-green-600 dark:text-green-400' : totalClosed === 0 ? 'text-gray-700 dark:text-gray-300' : 'text-red-500 dark:text-red-400')}>
              {placeholder ?? `${winRate}%`}
            </span>
            {!loading && totalClosed > 0 && (
              <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">({profitable}/{totalClosed})</span>
            )}
          </div>
        </div>

        {/* Positions */}
        <Link to="/schedule?view=positions" className="w-1/3 sm:w-auto sm:flex-1 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors rounded-r-lg">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Positions</div>
          <div className="text-sm font-semibold mt-1 text-gray-900 dark:text-white tabular-nums">
            {placeholder ?? totalPositions}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default TodayPnlCards;
